import React, { useState, useEffect, Fragment } from "react";
import cx from "classnames";
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import AuthService from "../../pages/Authentication/auth.service";
import NotificationService from "../../services/notification.service";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { messaging } from "../../config/firebase";
import { onMessage } from "firebase/messaging";
import { handleNotificationClick as redirectNotification } from "../../helpers/notificationRedirect";

import PerfectScrollbar from "react-perfect-scrollbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faAngleDown, faUser, faUserEdit } from "@fortawesome/free-solid-svg-icons";
import { IoIosGrid, IoIosNotifications} from "react-icons/io";
import Flag from "react-flagkit";

import city3 from "../../assets/utils/images/dropdown-header/city3.jpg";
import user from "../../assets/utils/images/avatars/user_gen.png";

import {
  DropdownToggle,
  DropdownMenu,
  Nav,
  Col,
  Row,
  Button,
  NavItem,
  NavLink,
  UncontrolledTooltip,
  UncontrolledButtonDropdown,
  UncontrolledDropdown,
  DropdownItem
} from "reactstrap";

library.add(
  faUser,
  faUserEdit
  );



const Topbar = () => {

  const { t, i18n } = useTranslation();

  useEffect(() => {
    setLanguageFlag(localStorage.getItem("language"));
    fetchNotifications();
    
    // Set up Firebase foreground message listener for real-time notifications
    let unsubscribeFirebase = null;
    
    // Function to set up the listener
    const setupMessageListener = () => {
      if (messaging) {
        try {
          unsubscribeFirebase = onMessage(messaging, (payload) => {
            console.log('[TOPBAR] Foreground notification received:', payload);
            
            // Create notification object from payload
            const newNotification = {
              _id: payload.data?._id || payload.data?.notification_id || new Date().getTime().toString(),
              notification: {
                title: payload.notification?.title || payload.data?.title || 'New Notification',
                body: payload.notification?.body || payload.data?.body || payload.data?.message || '',
                sound: 'default'
              },
              data: payload.data || {},
              read: false,
              created_at: new Date(),
              _isNew: true // Flag to identify newly received notifications
            };

            // Add to notifications list (prepend to show at top)
            setNotifications(prev => {
              // Check if notification already exists to avoid duplicates
              const exists = prev.some(n => n._id === newNotification._id);
              if (exists) return prev;
              return [newNotification, ...prev];
            });
            
            // Increment unread count
            setUnreadCount(prev => prev + 1);

            // Show browser notification if permission granted (optional - you can remove this if you don't want browser popup)
            if (Notification.permission === 'granted') {
              try {
                new Notification(payload.notification?.title || 'New Notification', {
                  body: payload.notification?.body || payload.data?.message || '',
                  icon: '/favicon.png',
                  badge: '/favicon.png',
                  tag: newNotification._id
                });
              } catch (notifError) {
                console.error('[TOPBAR] Error showing browser notification:', notifError);
              }
            }

            // Refresh notifications from server after a short delay to get the saved version
            setTimeout(() => {
              fetchNotifications();
            }, 1000);
          });
          console.log('[TOPBAR] Firebase foreground message listener set up');
        } catch (error) {
          console.error('[TOPBAR] Error setting up Firebase message listener:', error);
        }
      }
    };

    // Try to set up immediately if messaging is available
    setupMessageListener();

    // If messaging is not available yet, wait a bit and try again
    if (!messaging) {
      const checkInterval = setInterval(() => {
        if (messaging) {
          setupMessageListener();
          clearInterval(checkInterval);
        }
      }, 500);
      
      // Clear after 10 seconds if still not available
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 10000);
    }
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => {
      clearInterval(interval);
      if (unsubscribeFirebase) {
        unsubscribeFirebase();
      }
    };
  }, []);

  const fetchNotifications = () => {
    if (!currentUser) return;
    
    setLoadingNotifications(true);
    // Fetch only unread notifications, limit to 10 for topbar
    NotificationService.getNotifications({ 
      limit: 10,
      filter: { read: false } // Only unread
    })
      .then((response) => {
        setLoadingNotifications(false);
        if (response.data.status && response.data.data) {
          // Filter to show only unread and limit to 10
          const unreadNotifications = response.data.data
            .filter(n => !n.read)
            .slice(0, 10);
          setNotifications(unreadNotifications);
          setUnreadCount(response.data.unread_count || 0);
        }
      })
      .catch((error) => {
        setLoadingNotifications(false);
        console.error('Error fetching notifications:', error);
      });
  };

  const handleDeleteNotification = (notificationId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this notification?')) {
      NotificationService.deleteNotification(notificationId)
        .then((response) => {
          if (response.data.status) {
            fetchNotifications(); // Refresh list
          }
        })
        .catch((error) => {
          console.error('Error deleting notification:', error);
        });
    }
  };

  const handleMarkAsRead = (notificationId, showSuccess = false) => {
    NotificationService.markAsRead(notificationId)
      .then((response) => {
        if (response.data.status) {
          // Silently update - Topbar never shows success alerts
          fetchNotifications(); // Refresh list
        }
      })
      .catch((error) => {
        console.error('Error marking notification as read:', error);
      });
  };

  const handleNotificationClick = (notification) => {
    // Use redirect handler - it will mark as read and navigate
    const handled = redirectNotification(notification, navigate, handleMarkAsRead);
    if (!handled) {
      // If no redirect handler found, just mark as read if unread
      if (!notification.read) {
        handleMarkAsRead(notification._id);
      }
    }
  };

  const formatNotificationDate = (date) => {
    if (!date) return '';
    return moment(date).fromNow();
  };

  const getNotificationIcon = (notification) => {
    // Determine icon color based on notification type or priority
    if (notification.data?.priority === 'high') {
      return 'bg-danger';
    } else if (notification.read) {
      return 'bg-secondary';
    } else {
      return 'bg-success';
    }
  };

  const navigate = useNavigate();
  const currentUser = AuthService.getCurrentUser();
  const [logo, setLogo] = useState(localStorage.getItem("system_logo"));
  const [langFlag, setlangFlag] = useState('');
  const [active1, setActive1] = useState(false);
  const [active2, setActive2] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  function setLanguageFlag(lang) {
    if(lang == 'en') {
      setlangFlag('GB');
      setActive1(true)
      setActive2(false)
    }
    else {
      setlangFlag('MY');
      setActive1(false)
      setActive2(true)
    }
  };

  function changeLanguage(lang) {
    console.log('language:-', lang);
    localStorage.setItem('language', lang);
    setLanguageFlag(lang);
    i18n.changeLanguage(lang);
  }

  function logOut() {
    AuthService.logout();
    navigate("/login");
    window.location.reload();
  };

  return (
    <Fragment>
      <TransitionGroup>
        <CSSTransition component="div" 
        className={cx("app-header header-shadow")}
          appear={true} timeout={1500} enter={false} exit={false}>
          <div>  
            <div className="app-header__logo">
              <div>
              <img src={logo} style={{ width: '115px', height: 'auto' }} className="logo-src" alt="logo" />
              </div>
              <div className="header__pane ms-auto">
            </div>
          </div>
            <div
              className={cx("app-header__content", {
                // "header-mobile-open": false,
              })}>
              <div className="app-header-right">
                 <div className="header-dots">
                  {/* <UncontrolledDropdown>
                    <DropdownToggle className="p-0 me-2" color="link">
                      <div className="icon-wrapper icon-wrapper-alt rounded-circle">
                        <div className="icon-wrapper-bg bg-focus" />
                        <div className="language-icon">
                          <Flag className="me-3 opacity-8" country={langFlag} size="40" />
                        </div>
                      </div>
                    </DropdownToggle>
                    <DropdownMenu end className="rm-pointers">
                      <div className="dropdown-menu-header">
                        <div className="dropdown-menu-header-inner pt-4 pb-4 bg-focus">
                          <div className="menu-header-image opacity-05"
                            style={{
                              backgroundImage: "url(" + city2 + ")",
                            }}/>
                          <div className="menu-header-content text-center text-white">
                            <h6 className="menu-header-subtitle mt-0">
                              {t('Choose Language')}
                            </h6>
                          </div>
                        </div>
                      </div>
                      <DropdownItem active={active1} onClick={()=>changeLanguage('en')}>
                        <Flag className="me-3 opacity-8" country="GB"  />
                        English (UK)
                      </DropdownItem>
                      <DropdownItem active={active2} onClick={()=>changeLanguage('bm')}>
                        <Flag className="me-3 opacity-8" country="MY"  />
                        Malaysia
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown> */}
                  <UncontrolledDropdown>
                    <DropdownToggle className="p-0" color="link">
                    <div className="icon-wrapper icon-wrapper-alt rounded-circle position-relative">
                      <div className="icon-wrapper-bg bg-success" />
                        <IoIosNotifications color="#3ac47d" fontSize="23px" />
                        {unreadCount > 0 && (
                          <span className="badge badge-dot badge-dot-xl badge-danger position-absolute" 
                            style={{ 
                              top: '-5px', 
                              right: '-5px',
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              fontSize: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </div>
                    </DropdownToggle>
                    <DropdownMenu end className="rm-pointers dropdown-menu-lg" style={{ width: '350px' }}>
                      <div className="dropdown-menu-header mb-0" style={{ borderBottom: '1px solid #e9ecef' }}>
                        <div className="p-3">
                          <h5 className="mb-1" style={{ fontSize: '16px', fontWeight: '600', color: '#212529' }}>
                            {t('Notifications')}
                          </h5>
                          <h6 className="mb-0" style={{ fontSize: '13px', color: '#6c757d', fontWeight: '400' }}>
                            {unreadCount > 0 ? (
                              <>
                                {t('You have')} <b className="text-primary">{unreadCount}</b> {t('unread Notifications')}
                              </>
                            ) : (
                              t('No unread notifications')
                            )}
                          </h6>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '400px' }}>
                        <div className="scroll-area-sm" style={{ flex: 1, overflow: 'auto' }}>
                          <PerfectScrollbar>
                            <div className="p-0">
                              {loadingNotifications ? (
                                <div className="text-center py-4">
                                  <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              ) : notifications.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                  <i className="fa fa-bell-slash" style={{ fontSize: '32px', opacity: 0.3 }}></i>
                                  <p className="mt-2 mb-0">No unread notifications</p>
                                </div>
                              ) : (
                                <div className="list-group list-group-flush">
                                  {notifications.map((notification, index) => (
                                    <div
                                      key={notification._id || index}
                                      className="list-group-item list-group-item-action bg-light"
                                      style={{
                                        borderLeft: '3px solid #007bff',
                                        cursor: 'pointer',
                                        padding: '12px 16px',
                                        borderTop: index === 0 ? 'none' : '1px solid #e9ecef',
                                        transition: 'background-color 0.2s'
                                      }}
                                      onClick={() => handleNotificationClick(notification)}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                    >
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                          <div className="d-flex align-items-center mb-1">
                                            <h6 className="mb-0" style={{ 
                                              fontSize: '14px', 
                                              fontWeight: '600',
                                              color: '#212529',
                                              marginRight: '8px'
                                            }}>
                                              {notification.notification?.title || 'Notification'}
                                            </h6>
                                            <span className="badge bg-primary" style={{ fontSize: '9px', padding: '2px 6px' }}>
                                              New
                                            </span>
                                          </div>
                                          <p className="mb-1" style={{ 
                                            fontSize: '13px', 
                                            color: '#6c757d',
                                            lineHeight: '1.4',
                                            wordBreak: 'break-word'
                                          }}>
                                            {notification.notification?.body || notification.data?.message || 'No message'}
                                          </p>
                                          <small className="text-muted" style={{ fontSize: '11px' }}>
                                            {formatNotificationDate(notification.data?.generated_date || notification.created_at || notification._id?.getTimestamp?.())}
                                          </small>
                                        </div>
                                        <button
                                          className="btn btn-link btn-sm text-danger p-0 ms-2"
                                          style={{ 
                                            opacity: 0.6,
                                            fontSize: '14px',
                                            lineHeight: '1',
                                            minWidth: '20px'
                                          }}
                                          onClick={(e) => handleDeleteNotification(notification._id, e)}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                                          title="Delete"
                                        >
                                          <i className="fa fa-times"></i>
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </PerfectScrollbar>
                        </div>
                        {notifications.length > 0 && (
                          <div className="p-3 text-center border-top" style={{ flexShrink: 0, backgroundColor: '#fff' }}>
                            <Button
                              size="sm"
                              color="link"
                              onClick={() => navigate('/notifications')}
                              style={{ padding: 0 }}
                            >
                              {t('View All Notifications')} <i className="fa fa-arrow-right ms-1"></i>
                            </Button>
                          </div>
                        )}
                      </div>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div> 

                <div className="header-btn-lg pe-0">
                  <div className="widget-content p-0">
                    <div className="widget-content-wrapper">
                      <div className="widget-content-left">
                        <UncontrolledButtonDropdown>
                          <DropdownToggle color="link" className="p-0">
                            <img width={28} src={user} alt=""/>
                            <FontAwesomeIcon
                              className="ms-2 opacity-8"
                              icon={faAngleDown}
                            />
                          </DropdownToggle>
                          <DropdownMenu end className="rm-pointers dropdown-menu-lg">
                            <div className="dropdown-menu-header">
                              <div className="dropdown-menu-header-inner bg-info">
                                <div className="menu-header-image opacity-2"
                                  style={{
                                    backgroundImage: "url(" + city3 + ")",
                                  }}/>
                                <div className="menu-header-content text-start">
                                  <div className="widget-content p-0">
                                    <div className="widget-content-wrapper">
                                      <div className="widget-content-left me-3">
                                        <img width={62} className="rounded-circle" src={user} alt=""/>
                                      </div>
                                      <div className="widget-content-left">
                                        {currentUser &&
                                          <div className="widget-heading">
                                            {currentUser.name}
                                          </div>
                                        }
                                        <div className="widget-subheading opacity-8">
                                          Super Admin
                                        </div>
                                      </div>
                                      <div className="widget-content-right me-2">
                                        <Button className="btn-pill btn-shadow btn-custom-color btn-shine" onClick={logOut}>
                                        {t('Logout')}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="scroll-area-xs"
                              style={{
                                height: "80px",
                              }}>
                              <PerfectScrollbar>
                                <Nav vertical>
                                  {/* <NavItem className="nav-item-header">
                                  {t('My Account')}
                                  </NavItem> */}
                                  <NavItem>
                                    
                                    <NavLink href="/my-profile">
                                      <FontAwesomeIcon icon={faUserEdit} className="px-2 py-0" style={{marginBottom:"3px"}}/>
                                    {t('My Profile')}
                                    </NavLink>
                                  </NavItem>
                                  {/* <NavItem>
                                    <NavLink href="/home">
                                    {t('Settings')}
                                    </NavLink>
                                  </NavItem>
                                  <NavItem className="nav-item-header">
                                  {t('Activity')}
                                  </NavItem>
                                  <NavItem>
                                    <NavLink href="/home">
                                      {t('Logs')}
                                    </NavLink>
                                  </NavItem> */}
                                </Nav>
                              </PerfectScrollbar>
                            </div>
                            
                          </DropdownMenu>
                        </UncontrolledButtonDropdown>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </Fragment>
  );







  // const navigate = useNavigate();
  // const currentUser = AuthService.getCurrentUser();
  
  // const [showModeratorBoard, setShowModeratorBoard] = useState(false);
  // const [showAdminBoard, setShowAdminBoard] = useState(false);

  // useEffect(() => {

  //   if (currentUser) {
  //     setShowModeratorBoard(currentUser.roles.includes("ROLE_MODERATOR"));
  //     setShowAdminBoard(currentUser.roles.includes("ROLE_ADMIN"));
  //   }
    

  //   EventBus.on("logout", () => {
  //     logOut();
  //   });

  //   return () => {
  //     EventBus.remove("logout");
  //   };
  // }, []);

  // function gotoprofile() {
  //   navigate("/update-profile");
  //   // window.location.reload();
  // }

  // function gotosettings() {
  //   navigate("/settings");
  //   // window.location.reload();
  // }

  // function logOut() {
  //   AuthService.logout();
  //   setShowModeratorBoard(false);
  //   setShowAdminBoard(false);
  //   navigate("/login");
  //   window.location.reload();
  // };

  // const baseURL = process.env.PUBLIC_URL;

  // // render() {
  //   return (
  //     <div className="topbar">
      
  //       <nav className="navbar-custom" id="navbar-custom">
  //         <ul className="list-unstyled topbar-nav float-end mb-0">
  //           <li className="dropdown notification-list">
  //             <a
  //               className="nav-link dropdown-toggle arrow-none nav-icon"
  //               data-bs-toggle="dropdown"
  //               href="/home"
  //               role="button"
  //               aria-haspopup="false"
  //               aria-expanded="false"
  //             >
  //               <i className="ti ti-bell" />
  //               <span className="alert-badge" />
  //             </a>
  //             <div className="dropdown-menu dropdown-menu-end dropdown-lg pt-0">
  //               <h6 className="dropdown-item-text font-15 m-0 py-3 border-bottom d-flex justify-content-between align-items-center">
  //                 Notifications{" "}
  //                 <span className="badge bg-soft-primary badge-pill">2</span>
  //               </h6>
  //               <div className="notification-menu" data-simplebar>
                
  //                 <a href="/home" className="dropdown-item py-3">
  //                   <small className="float-end text-muted ps-2">
  //                     2 min ago
  //                   </small>
  //                   <div className="media">
  //                     <div className="avatar-md bg-soft-primary">
  //                       <i className="ti ti-chart-arcs" />
  //                     </div>
  //                     <div className="media-body align-self-center ms-2 text-truncate">
  //                       <h6 className="my-0 fw-normal text-dark">
  //                         Your order is placed
  //                       </h6>
  //                       <small className="text-muted mb-0">
  //                         Dummy text of the printing and industry.
  //                       </small>
  //                     </div>
                    
  //                   </div>
                  
  //                 </a>
                
                
  //                 <a href="/home" className="dropdown-item py-3">
  //                   <small className="float-end text-muted ps-2">
  //                     10 min ago
  //                   </small>
  //                   <div className="media">
  //                     <div className="avatar-md bg-soft-primary">
  //                       <i className="ti ti-device-computer-camera" />
  //                     </div>
  //                     <div className="media-body align-self-center ms-2 text-truncate">
  //                       <h6 className="my-0 fw-normal text-dark">
  //                         Meeting with designers
  //                       </h6>
  //                       <small className="text-muted mb-0">
  //                         It is a long established fact that a reader.
  //                       </small>
  //                     </div>
                    
  //                   </div>
                  
  //                 </a>
                
                
  //                 <a href="/home" className="dropdown-item py-3">
  //                   <small className="float-end text-muted ps-2">
  //                     40 min ago
  //                   </small>
  //                   <div className="media">
  //                     <div className="avatar-md bg-soft-primary">
  //                       <i className="ti ti-diamond" />
  //                     </div>
  //                     <div className="media-body align-self-center ms-2 text-truncate">
  //                       <h6 className="my-0 fw-normal text-dark">
  //                         UX 3 Task complete.
  //                       </h6>
  //                       <small className="text-muted mb-0">
  //                         Dummy text of the printing.
  //                       </small>
  //                     </div>
                    
  //                   </div>
                  
  //                 </a>
                
                
  //                 <a href="/home" className="dropdown-item py-3">
  //                   <small className="float-end text-muted ps-2">
  //                     1 hr ago
  //                   </small>
  //                   <div className="media">
  //                     <div className="avatar-md bg-soft-primary">
  //                       <i className="ti ti-drone" />
  //                     </div>
  //                     <div className="media-body align-self-center ms-2 text-truncate">
  //                       <h6 className="my-0 fw-normal text-dark">
  //                         Your order is placed
  //                       </h6>
  //                       <small className="text-muted mb-0">
  //                         It is a long established fact that a reader.
  //                       </small>
  //                     </div>
                    
  //                   </div>
                  
  //                 </a>
                
                
  //                 <a href="/home" className="dropdown-item py-3">
  //                   <small className="float-end text-muted ps-2">
  //                     2 hrs ago
  //                   </small>
  //                   <div className="media">
  //                     <div className="avatar-md bg-soft-primary">
  //                       <i className="ti ti-users" />
  //                     </div>
  //                     <div className="media-body align-self-center ms-2 text-truncate">
  //                       <h6 className="my-0 fw-normal text-dark">
  //                         Payment Successfull
  //                       </h6>
  //                       <small className="text-muted mb-0">
  //                         Dummy text of the printing.
  //                       </small>
  //                     </div>
                    
  //                   </div>
                  
  //                 </a>
                
  //               </div>
              
  //               <a
  //                 href="/home"
  //                 className="dropdown-item text-center text-primary"
  //               >
  //                 View all <i className="fi-arrow-right" />
  //               </a>
  //             </div>
  //           </li>
  //           <li className="dropdown">
  //             <a
  //               className="nav-link dropdown-toggle nav-user"
  //               data-bs-toggle="dropdown"
  //               href="/home"
  //               role="button"
  //               aria-haspopup="false"
  //               aria-expanded="false"
  //             >
  //               <div className="d-flex align-items-center">
  //                 <img
  //                   src={baseURL+"../assets/images/users/user-4.jpg"}
  //                   alt="profile-user"
  //                   className="rounded-circle me-2 thumb-sm"
  //                 />
  //                 <div>
                    
  //                 <UncontrolledButtonDropdown>
  //                   <DropdownToggle caret="true" className="mb-2 me-2" color="link">
  //                   <small className="d-none d-md-block font-11">Admin
  //                   </small>
  //                   <span className="d-none d-md-block fw-semibold font-12">
  //                   {currentUser.name} <i className="mdi mdi-chevron-down" />
  //                   </span>
  //                   </DropdownToggle>
  //                   <DropdownMenu className="dropdown-menu-rounded">
  //                     <DropdownItem onClick={gotoprofile}>Profile</DropdownItem>
  //                     <DropdownItem onClick={gotosettings}>Settings</DropdownItem>
  //                     <DropdownItem onClick={logOut}>Logout</DropdownItem>
  //                   </DropdownMenu>
  //                 </UncontrolledButtonDropdown>
  //                   {/* <small className="d-none d-md-block font-11">Admin
  //                   </small>
  //                   <span className="d-none d-md-block fw-semibold font-12">
  //                   {currentUser.name} <i className="mdi mdi-chevron-down" />
  //                   </span> */}
  //                 </div>
  //               </div>
  //             </a>
              
  //             {/* <div className="dropdown-menu dropdown-menu-end">
  //               <a className="dropdown-item" href={"/update-profile"}>
  //                 <i className="ti ti-user font-16 me-1 align-text-bottom" />
  //                 Profile
  //               </a>
  //               <a className="dropdown-item" href="/settings">
  //                 <i className="ti ti-settings font-16 me-1 align-text-bottom" />
  //                 Settings
  //               </a>
  //               <div className="dropdown-divider mb-0" />
  //               <a className="dropdown-item" href={"/login"} onClick={logOut}>
  //                 <i className="ti ti-power font-16 me-1 align-text-bottom" />
  //                 Logout
  //               </a>
  //             </div> */}
  //           </li>
          
  //         </ul>
        
  //         <ul className="list-unstyled topbar-nav mb-0">
  //           <li>
  //             <button
  //               className="nav-link button-menu-mobile nav-icon"
  //               id="togglemenu"
  //             >
  //               <i className="ti ti-menu-2" />
  //             </button>
  //           </li>
  //         </ul>
  //       </nav>
      
  //     </div>
  //   );
    // }
  }
  
  export default Topbar;
  